import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { PropertyWizardHeader } from '../components/property-wizard/PropertyWizardHeader';
import { StepModePicker, WizardPath } from '../components/property-wizard/StepModePicker';
import { StepLink } from '../components/property-wizard/StepLink';
import { StepName } from '../components/property-wizard/StepName';
import { StepHousingRooms } from '../components/property-wizard/StepHousingRooms';
import { StepLocation } from '../components/property-wizard/StepLocation';
import { StepFloorYear } from '../components/property-wizard/StepFloorYear';
import { StepPriceDuration } from '../components/property-wizard/StepPriceDuration';
import { StepLifestylePets } from '../components/property-wizard/StepLifestylePets';
import { StepFacilities } from '../components/property-wizard/StepFacilities';
import { StepPhotos } from '../components/property-wizard/StepPhotos';
import { StepVerification } from '../components/property-wizard/StepVerification';
import { StepOfferDetails, ContactMethod } from '../components/property-wizard/StepOfferDetails';
import { SuccessScreen } from '../components/property-wizard/SuccessScreen';
import { PAGE_ROUTE } from '../lib/routes';
import { getSession, getPropertyById, getMyProperties, saveProperty, updateProperty, findOrCreatePropertyByFacilities, findLatestOfferFromTo, tagOfferWithProperty, saveOffer, markApplicationSent, type SavedProperty, type SavedPropertyFacilities, type StoredOffer } from '../lib/utils';
import { PROPERTY_FACILITY_CATALOG } from '../lib/constants';
const DRAFT_KEY = 'izio_property_wizard_v1';
type WizardMode = 'standalone' | 'offer';
type SubMode = 'create' | 'edit';
interface WizardContext {
  mode?: WizardMode;
  subMode?: SubMode;
  propertyId?: string;
  tenantProfileId?: string;
  tenantName?: string;
  tenantWishlist?: {
    locations: string[];
    essentials: string[];
    preferred: string[];
  };
}
interface PropertyWizardProps {
  navigate: (page: string, context?: any) => void;
  context?: WizardContext;
}
interface WizardData {
  name: string;
  link: string;
  housingType: string;
  rooms: string;
  // Structured location value matching LOCATION_TREE format:
  // "București|Sector 1|Floreasca" or "Cluj-Napoca|Centru" or just "București".
  location: string;
  floor: string;
  constructionYear: string;
  priceEur: string;
  contractDuration: string;
  petsAllowed: boolean;
  smokingAllowed: boolean;
  facilities: string[];
  photos: string[];
  verificationFile: File | null;
  // Offer-only
  rentEur: string;
  depositEur: string;
  contactMethod: ContactMethod;
  contactEmail: string;
  contactPhone: string;
  message: string;
  termsAccepted: boolean;
}
const EMPTY_DATA: WizardData = {
  name: '',
  link: '',
  housingType: '',
  rooms: '',
  location: '',
  floor: '',
  constructionYear: '',
  priceEur: '',
  contractDuration: '',
  petsAllowed: false,
  smokingAllowed: false,
  facilities: [],
  photos: [],
  verificationFile: null,
  rentEur: '',
  depositEur: '',
  contactMethod: 'email',
  contactEmail: '',
  contactPhone: '',
  message: '',
  termsAccepted: false
};
type Step = 'mode' | 'link' | 'name' | 'housingRooms' | 'location' | 'floorYear' | 'priceDuration' | 'lifestyle' | 'facilities' | 'photos' | 'verification' | 'offerDetails' | 'success';
function buildStepFlow(path: WizardPath | null, mode: WizardMode, isAgent: boolean): Step[] {
  if (path === null) return ['mode'];
  if (path === 'quick') {
    const base: Step[] = ['mode', 'link', 'facilities'];
    if (mode === 'offer') base.push('offerDetails', 'success');
    return base;
  }
  // personalized
  const base: Step[] = ['mode', 'name', 'housingRooms', 'location', 'floorYear', 'priceDuration', 'lifestyle', 'facilities', 'photos'];
  if (!isAgent) base.push('verification');
  if (mode === 'offer') base.push('offerDetails', 'success');
  return base;
}
// Compose a structured location string from a SavedProperty's city +
// neighborhood. The wizard now stores a single "City|Sector|Zone" string,
// so we map the legacy fields into that shape on prefill and back into
// city + neighborhood on save (for the existing SavedProperty schema).
function composeLocation(city?: string, neighborhood?: string): string {
  if (!city && !neighborhood) return '';
  if (city && neighborhood) return `${city}|${neighborhood}`;
  return city || neighborhood || '';
}
function splitLocation(location: string): {
  city?: string;
  neighborhood?: string;
} {
  if (!location) return {};
  const parts = location.split('|');
  if (parts.length === 1) return {
    city: parts[0]
  };
  // For "City|Sector|Zone" (București) we treat sector+zone joined as
  // the neighborhood label so it appears intact on cards.
  const [city, ...rest] = parts;
  return {
    city,
    neighborhood: rest.join(' · ')
  };
}
function partitionFacilities(selected: string[]): SavedPropertyFacilities {
  const location: string[] = [];
  const essential: string[] = [];
  const preferred: string[] = [];
  selected.forEach(item => {
    if ((PROPERTY_FACILITY_CATALOG.location as readonly string[]).includes(item)) {
      location.push(item);
    } else if ((PROPERTY_FACILITY_CATALOG.preferred as readonly string[]).includes(item)) {
      preferred.push(item);
    } else {
      essential.push(item);
    }
  });
  return {
    location,
    essential,
    preferred
  };
}
export function PropertyWizard({
  navigate,
  context
}: PropertyWizardProps) {
  const mode: WizardMode = context?.mode || 'standalone';
  const subMode: SubMode = context?.subMode || 'create';
  const editingPropertyId = context?.propertyId;
  const session = useMemo(() => getSession(), []);
  const isAgent = session?.subRole === 'agent';
  const [path, setPath] = useState<WizardPath | null>(null);
  const [step, setStep] = useState<Step>('mode');
  const [data, setData] = useState<WizardData>(EMPTY_DATA);
  const [editingProperty, setEditingProperty] = useState<SavedProperty | null>(null);
  const [savedProperties, setSavedProperties] = useState<SavedProperty[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  // Auto-save result for offer success screen
  const [autoSaveResult, setAutoSaveResult] = useState<{
    property: SavedProperty;
    matchType: 'new' | 'name' | 'signature';
  } | null>(null);
  // Load saved properties (for picker on mode step)
  useEffect(() => {
    if (session?.email) {
      setSavedProperties(getMyProperties(session.email));
    }
  }, [session?.email]);
  // Edit mode: prefill + skip mode picker
  useEffect(() => {
    if (subMode === 'edit' && editingPropertyId) {
      const p = getPropertyById(editingPropertyId);
      if (!p) {
        navigate(PAGE_ROUTE.DASHBOARD_PROFIL);
        return;
      }
      const inferredPath: WizardPath = p.presentationMode === 'photos' ? 'personalized' : 'quick';
      setEditingProperty(p);
      setPath(inferredPath);
      setData({
        ...EMPTY_DATA,
        name: p.name || '',
        link: p.link || '',
        housingType: p.housingType || '',
        rooms: p.rooms || '',
        location: composeLocation(p.city, p.neighborhood),
        floor: p.floor || '',
        constructionYear: p.constructionYear || '',
        priceEur: p.priceEur ? String(p.priceEur) : '',
        contractDuration: p.contractDuration || '',
        petsAllowed: !!p.petsAllowed,
        smokingAllowed: !!p.smokingAllowed,
        facilities: [...(p.facilities.location || []), ...(p.facilities.essential || []), ...(p.facilities.preferred || [])],
        photos: p.photos || []
      });
      // Jump to first content step
      setStep(inferredPath === 'quick' ? 'link' : 'name');
    }
  }, [subMode, editingPropertyId, navigate]);
  // Prefill offer contact fields from session
  useEffect(() => {
    if (mode === 'offer' && session) {
      setData(d => ({
        ...d,
        contactEmail: d.contactEmail || session.email || '',
        contactPhone: d.contactPhone || session.phone || ''
      }));
    }
  }, [mode, session]);
  // Draft persistence (300ms debounce)
  useEffect(() => {
    if (subMode === 'edit') return; // don't draft when editing
    const t = setTimeout(() => {
      try {
        const draft = {
          mode,
          path,
          step,
          data: {
            ...data,
            verificationFile: null
          },
          tenantId: context?.tenantProfileId,
          draftedAt: new Date().toISOString()
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [data, path, step, mode, context?.tenantProfileId, subMode]);
  const flow = useMemo(() => buildStepFlow(path, mode, isAgent), [path, mode, isAgent]);
  const stepIndex = flow.indexOf(step);
  const totalContentSteps = flow.filter(s => s !== 'mode' && s !== 'success').length;
  const contentStepIndex = flow.slice(0, stepIndex).filter(s => s !== 'mode' && s !== 'success').length;
  const updateData = (patch: Partial<WizardData>) => {
    setData(d => ({
      ...d,
      ...patch
    }));
  };
  const goNext = () => {
    const next = flow[stepIndex + 1];
    if (next) {
      setStep(next);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    // No next step. Defensive fallback: if we're in standalone mode, this
    // is the final content step → save now. This guarantees Continuă is
    // never a dead button even if `isOnFinalSaveStep` somehow misfires.
    if (mode === 'standalone') {
      handleStandaloneSave();
    }
  };
  const goPrev = () => {
    const prev = flow[stepIndex - 1];
    if (prev) {
      setStep(prev);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const pickPath = (p: WizardPath) => {
    setPath(p);
    setStep(p === 'quick' ? 'link' : 'name');
  };
  const pickSavedProperty = (p: SavedProperty) => {
    const inferred: WizardPath = p.presentationMode === 'photos' ? 'personalized' : 'quick';
    setPath(inferred);
    setData({
      ...EMPTY_DATA,
      ...data,
      name: p.name || '',
      link: p.link || '',
      housingType: p.housingType || '',
      rooms: p.rooms || '',
      location: composeLocation(p.city, p.neighborhood),
      floor: p.floor || '',
      constructionYear: p.constructionYear || '',
      priceEur: p.priceEur ? String(p.priceEur) : '',
      contractDuration: p.contractDuration || '',
      petsAllowed: !!p.petsAllowed,
      smokingAllowed: !!p.smokingAllowed,
      facilities: [...(p.facilities.location || []), ...(p.facilities.essential || []), ...(p.facilities.preferred || [])],
      photos: p.photos || []
    });
    // Skip straight to facilities
    setStep('facilities');
  };
  const isStepValid = (): boolean => {
    switch (step) {
      case 'link':
        return data.link.trim().length > 0;
      case 'name':
        return data.name.trim().length > 0;
      case 'housingRooms':
        return !!data.housingType && !!data.rooms;
      case 'location':
        return !!data.location;
      case 'floorYear':
      case 'priceDuration':
      case 'lifestyle':
      case 'photos':
      case 'verification':
        return true;
      case 'facilities':
        return data.facilities.length > 0;
      case 'offerDetails':
        return data.rentEur.trim().length > 0 && data.depositEur.trim().length > 0 && data.contactEmail.trim().length > 0 && (data.contactMethod !== 'phone' || data.contactPhone.trim().length > 0) && data.message.trim().length > 0 && data.termsAccepted;
      default:
        return true;
    }
  };
  const buildFacilitiesPayload = (): SavedPropertyFacilities => partitionFacilities(data.facilities);
  const buildPropertyName = (): string => {
    const trimmed = data.name.trim();
    if (trimmed) return trimmed;
    const {
      city
    } = splitLocation(data.location);
    const cityPart = city || 'Proprietate';
    const roomsPart = data.rooms ? ` · ${data.rooms} cam` : '';
    return `${cityPart}${roomsPart}`;
  };
  // Standalone save
  const handleStandaloneSave = () => {
    if (!session?.email) return;
    setSubmitting(true);
    try {
      const {
        city,
        neighborhood
      } = splitLocation(data.location);
      const payload = {
        ownerEmail: session.email,
        name: buildPropertyName(),
        housingType: data.housingType || undefined,
        rooms: data.rooms || undefined,
        floor: data.floor || undefined,
        constructionYear: data.constructionYear || undefined,
        city: city || undefined,
        neighborhood: neighborhood || undefined,
        priceEur: data.priceEur ? parseInt(data.priceEur, 10) : undefined,
        contractDuration: data.contractDuration || undefined,
        petsAllowed: data.petsAllowed,
        smokingAllowed: data.smokingAllowed,
        presentationMode: (path === 'personalized' ? 'photos' : 'link') as 'photos' | 'link',
        link: data.link.trim() || undefined,
        photos: data.photos,
        thumbnail: data.photos[0],
        facilities: buildFacilitiesPayload(),
        isActive: true
      };
      if (subMode === 'edit' && editingProperty) {
        updateProperty(editingProperty.id, payload as any);
      } else {
        saveProperty(payload as any);
      }
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {}
      navigate(PAGE_ROUTE.DASHBOARD_PROFIL);
    } finally {
      setSubmitting(false);
    }
  };
  // Offer send + auto-save property
  const handleOfferSend = () => {
    if (!session?.email || !context?.tenantProfileId || !context.tenantName) return;
    setSubmitting(true);
    try {
      // 1) Auto-save / link property
      const facilities = buildFacilitiesPayload();
      const result = findOrCreatePropertyByFacilities(session.email, facilities, {
        name: buildPropertyName(),
        link: data.link.trim() || undefined,
        photos: data.photos,
        thumbnail: data.photos[0]
      });
      // 2) Send offer
      const matchedFeatures = data.facilities;
      const wishlistTotal = context.tenantWishlist ? context.tenantWishlist.locations.length + context.tenantWishlist.essentials.length + context.tenantWishlist.preferred.length : 0;
      const wishlistMatched = context.tenantWishlist ? matchedFeatures.filter(f => [...context.tenantWishlist!.locations, ...context.tenantWishlist!.essentials, ...context.tenantWishlist!.preferred].includes(f)).length : 0;
      const matchScore = wishlistTotal > 0 ? `${wishlistMatched}/${wishlistTotal}` : undefined;
      const offer: StoredOffer = {
        id: `offer_${Date.now()}`,
        status: 'new',
        fromEmail: session.email,
        fromName: session.name,
        fromAvatar: session.avatar,
        toSlug: context.tenantProfileId,
        toName: context.tenantName,
        sentAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        message: data.message,
        contactEmail: data.contactEmail || undefined,
        contactPhone: data.contactPhone || undefined,
        matchScore,
        matchedFeatures,
        listingPreview: path === 'quick' || data.link ? {
          title: result.property.name,
          priceLabel: data.rentEur ? `${data.rentEur} €` : 'Vezi anunț',
          locationLabel: splitLocation(data.location).city || 'București',
          url: data.link || 'https://izio.ro'
        } : undefined,
        photos: data.photos.length > 0 ? data.photos.slice(0, 1) : undefined
      };
      saveOffer(offer);
      markApplicationSent(context.tenantProfileId);
      // 3) Tag offer with property
      tagOfferWithProperty(offer.id, result.property.id);
      // 4) Done
      setAutoSaveResult({
        property: result.property,
        matchType: result.matchType
      });
      localStorage.removeItem(DRAFT_KEY);
      setStep('success');
    } finally {
      setSubmitting(false);
    }
  };
  const handleClose = () => {
    // If draft has content, confirm
    const hasContent = data.link || data.name || data.facilities.length > 0 || data.photos.length > 0 || data.message;
    if (hasContent && step !== 'success') {
      setShowCancelDialog(true);
    } else {
      localStorage.removeItem(DRAFT_KEY);
      navigate(mode === 'offer' && context?.tenantProfileId ? PAGE_ROUTE.TENANT_ANUNT : PAGE_ROUTE.DASHBOARD_PROFIL, mode === 'offer' && context?.tenantProfileId ? {
        slug: context.tenantProfileId
      } : undefined);
    }
  };
  const confirmClose = () => {
    localStorage.removeItem(DRAFT_KEY);
    setShowCancelDialog(false);
    navigate(mode === 'offer' && context?.tenantProfileId ? PAGE_ROUTE.TENANT_ANUNT : PAGE_ROUTE.DASHBOARD_PROFIL, mode === 'offer' && context?.tenantProfileId ? {
      slug: context.tenantProfileId
    } : undefined);
  };
  // Header title
  const headerTitle = subMode === 'edit' ? 'Editează proprietatea' : mode === 'offer' ? `Trimite ofertă${context?.tenantName ? ` către ${context.tenantName}` : ''}` : 'Adaugă proprietate';
  // Footer primary action
  const isLastContentStep = flow[stepIndex + 1] === 'success' || stepIndex === flow.length - 1;
  // Final-save step = the last content step in the current flow, in
  // standalone mode. We derive it from the flow itself (rather than
  // enumerating cases) so it stays correct regardless of how the flow
  // changes per path / agent / offer mode.
  const nextStep = flow[stepIndex + 1];
  const isLastContentStepInFlow = stepIndex === flow.length - 1 || nextStep === 'success';
  const isOnFinalSaveStep = mode === 'standalone' && path !== null && isLastContentStepInFlow;
  const isOfferDetails = step === 'offerDetails';
  const primaryLabel = isOfferDetails ? path === 'personalized' ? 'Trimite oferta personalizată' : 'Trimite oferta' : isOnFinalSaveStep ? subMode === 'edit' ? 'Salvează modificările' : 'Salvează proprietatea' : 'Continuă';
  const primaryAction = () => {
    if (isOfferDetails) return handleOfferSend();
    if (isOnFinalSaveStep) return handleStandaloneSave();
    goNext();
  };
  // Sari peste on the verification step: drop the staged file (if any) and
  // then run the primary action — which is "save the property" in standalone
  // mode (the final step) or "advance to offerDetails" in offer mode.
  const handleSkipVerification = () => {
    if (data.verificationFile) {
      setData(d => ({
        ...d,
        verificationFile: null
      }));
    }
    if (isOnFinalSaveStep) {
      // Save immediately without the verification file.
      if (!session?.email) return;
      setSubmitting(true);
      try {
        const {
          city,
          neighborhood
        } = splitLocation(data.location);
        const payload = {
          ownerEmail: session.email,
          name: buildPropertyName(),
          housingType: data.housingType || undefined,
          rooms: data.rooms || undefined,
          floor: data.floor || undefined,
          constructionYear: data.constructionYear || undefined,
          city: city || undefined,
          neighborhood: neighborhood || undefined,
          priceEur: data.priceEur ? parseInt(data.priceEur, 10) : undefined,
          contractDuration: data.contractDuration || undefined,
          petsAllowed: data.petsAllowed,
          smokingAllowed: data.smokingAllowed,
          presentationMode: (path === 'personalized' ? 'photos' : 'link') as 'photos' | 'link',
          link: data.link.trim() || undefined,
          photos: data.photos,
          thumbnail: data.photos[0],
          facilities: buildFacilitiesPayload(),
          isActive: true
        };
        if (subMode === 'edit' && editingProperty) {
          updateProperty(editingProperty.id, payload as any);
        } else {
          saveProperty(payload as any);
        }
        localStorage.removeItem(DRAFT_KEY);
        navigate(PAGE_ROUTE.DASHBOARD_PROFIL);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    goNext();
  };
  const showFooter = step !== 'mode' && step !== 'success';
  const showVerificationSkip = step === 'verification';
  // Progress (0-100), only on content steps
  const progressPct = totalContentSteps === 0 ? 0 : Math.min(100, (contentStepIndex + (step === 'success' ? 1 : 0)) / totalContentSteps * 100);
  return <div className="min-h-screen bg-dark-1 flex flex-col" data-id="element-3802">
      <PropertyWizardHeader title={headerTitle} canGoBack={step !== 'mode' && step !== 'success' && stepIndex > 0} onBack={goPrev} onClose={handleClose} data-id="element-3803" />

      {step !== 'mode' && step !== 'success' && <div className="w-full h-1 bg-dark-2 overflow-hidden" data-id="element-3804">
          <div className="h-full bg-lime-1 transition-all duration-500 ease-out" style={{
        width: `${progressPct}%`
      }} data-id="element-3805" />
        </div>}

      <ConfirmDialog open={showCancelDialog} title="Renunți la proprietate?" description="Tot ce ai completat va fi pierdut. Această acțiune nu poate fi anulată." confirmLabel="Da, închide" cancelLabel="Continuă editarea" variant="danger" onConfirm={confirmClose} onCancel={() => setShowCancelDialog(false)} data-id="element-3806" />

      <main className="flex-1 flex flex-col w-full" data-id="element-3807">
        <div className="flex-1 w-full max-w-xl mx-auto px-6 py-8 sm:py-16 pb-40 lg:pb-8 flex flex-col" data-id="element-3808">
          <div className="flex-1 flex flex-col justify-center" data-id="element-3809">
            {step === 'mode' && <StepModePicker tenantName={mode === 'offer' ? context?.tenantName : undefined} tenantWishlist={mode === 'offer' ? context?.tenantWishlist : undefined} savedProperties={mode === 'offer' ? savedProperties.filter(p => p.isActive) : []} onPick={pickPath} onPickSaved={pickSavedProperty} data-id="element-3810" />}

            {step === 'link' && <StepLink link={data.link} name={data.name} onChange={updateData} data-id="element-3811" />}

            {step === 'name' && <StepName name={data.name} onChange={name => updateData({
            name
          })} data-id="element-3812" />}

            {step === 'housingRooms' && <StepHousingRooms housingType={data.housingType} rooms={data.rooms} onChange={updateData} data-id="element-3813" />}

            {step === 'location' && <StepLocation location={data.location} onChange={updateData} data-id="element-3814" />}

            {step === 'floorYear' && <StepFloorYear housingType={data.housingType} floor={data.floor} constructionYear={data.constructionYear} onChange={updateData} data-id="element-3815" />}

            {step === 'priceDuration' && <StepPriceDuration priceEur={data.priceEur} contractDuration={data.contractDuration} onChange={updateData} data-id="element-3816" />}

            {step === 'lifestyle' && <StepLifestylePets petsAllowed={data.petsAllowed} smokingAllowed={data.smokingAllowed} onChange={updateData} data-id="element-3817" />}

            {step === 'facilities' && <StepFacilities selected={data.facilities} onChange={facilities => updateData({
            facilities
          })} tenantWishlist={mode === 'offer' ? context?.tenantWishlist : undefined} tenantName={mode === 'offer' ? context?.tenantName : undefined} data-id="element-3818" />}

            {step === 'photos' && <StepPhotos photos={data.photos} onChange={photos => updateData({
            photos
          })} data-id="element-3819" />}

            {step === 'verification' && <StepVerification verificationFile={data.verificationFile} verificationStatus={editingProperty?.verificationStatus} ownerName={session?.name} onChange={verificationFile => updateData({
            verificationFile
          })} data-id="element-3820" />}

            {step === 'offerDetails' && <StepOfferDetails rentEur={data.rentEur} depositEur={data.depositEur} contactMethod={data.contactMethod} contactEmail={data.contactEmail} contactPhone={data.contactPhone} message={data.message} termsAccepted={data.termsAccepted} tenantName={context?.tenantName || 'chiriaș'} onChange={updateData} data-id="element-3821" />}

            {step === 'success' && <SuccessScreen tenantName={context?.tenantName || 'Chiriașul'} property={autoSaveResult?.property || null} matchType={autoSaveResult?.matchType || null} onClose={() => navigate(PAGE_ROUTE.TENANT_ANUNT, {
            slug: context?.tenantProfileId
          })} onViewProperty={id => navigate(PAGE_ROUTE.DASHBOARD_PROFIL)} data-id="element-3822" />}
          </div>

          {/* Desktop inline footer */}
          {showFooter && <div className="hidden lg:flex items-center justify-between mt-12 pt-6 border-t border-dark-3" data-id="element-3823">
              <span className="text-xs text-light-4" data-id="element-3824">
                Pasul {contentStepIndex + 1} din {totalContentSteps}
              </span>
              <div className="flex items-center gap-3" data-id="element-3825">
                {showVerificationSkip && <button type="button" onClick={handleSkipVerification} disabled={submitting} className="text-sm font-medium text-light-3 hover:text-light-1 px-3 h-11 transition-colors cursor-pointer disabled:opacity-50" data-id="element-3826">
                    Sari peste
                  </button>}
                <Button onClick={primaryAction} disabled={!isStepValid() || submitting} className="bg-lime-1 text-dark-1 hover:bg-lime-2 px-8 disabled:opacity-50 h-11 font-semibold" data-id="element-3827">
                  {submitting ? 'Se trimite...' : primaryLabel}
                </Button>
              </div>
            </div>}
        </div>
      </main>

      {/* Mobile sticky footer */}
      {showFooter && <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-dark-1/90 backdrop-blur-md border-t border-dark-3 p-4 z-40" data-id="element-3828">
          <div className="max-w-xl mx-auto flex items-center justify-between gap-3" data-id="element-3829">
            <span className="text-xs text-light-4 shrink-0" data-id="element-3830">
              {contentStepIndex + 1}/{totalContentSteps}
            </span>
            <div className="flex items-center gap-2" data-id="element-3831">
              {showVerificationSkip && <button type="button" onClick={handleSkipVerification} disabled={submitting} className="text-sm font-medium text-light-3 hover:text-light-1 px-3 h-11 transition-colors cursor-pointer disabled:opacity-50" data-id="element-3832">
                  Sari peste
                </button>}
              <Button onClick={primaryAction} disabled={!isStepValid() || submitting} className="bg-lime-1 text-dark-1 hover:bg-lime-2 px-6 disabled:opacity-50 h-11 font-semibold" data-id="element-3833">
                {submitting ? '...' : primaryLabel}
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}